import { helpElement } from '../../help/attrs';
import { useSearchParams } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper,
  Tab, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tabs, TextField,
  Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { BankAccountService } from '../../services/bankAccount.service';
import { AccountService } from '../../services/account.service';
import { CustomerService } from '../../services/customer.service';
import { SalesInvoiceService } from '../../services/salesInvoice.service';
import { SupplierBillService } from '../../services/supplierBill.service';
import { ChequeBookService, ChequeLayoutService, ChequeService } from '../../services/cheque.service';
import { PERMISSIONS } from '../../constants/permissions';
import { BankAccount } from '../../interfaces/bank/bankAccount.interface';
import {
  CHEQUE_STATUS_COLORS, Cheque, ChequeBook, ChequeDirection, ChequeLayout, ChequeStatus,
} from '../../interfaces/bank/cheque.interface';
import { Account } from '../../interfaces/accounting.interface';
import { Customer } from '../../interfaces/sales/customer.interface';
import { SalesInvoice } from '../../interfaces/sales/salesInvoice.interface';
import { SupplierBill } from '../../interfaces/procurement/supplierBill.interface';
import { SearchableSelect } from '../common/SearchableSelect';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { leafAccounts } from '../../utils/postingAccounts';
import { PaymentWithholdingSection, PaymentWithholdingValue } from '../procurement/withholding/PaymentWithholdingSection'; // B4-0 (0-3)

const futureDated = (c: Cheque) => c.chequeDate > toLocalIsoDate();

type DialogKind =
  | { kind: 'registerReceived' } | { kind: 'registerIssued' }
  | { kind: 'deposit'; cheque: Cheque } | { kind: 'bounce'; cheque: Cheque }
  | { kind: 'release'; cheque: Cheque } | { kind: 'print'; cheque: Cheque }
  | { kind: 'reason'; cheque: Cheque; action: 'cancel' | 'stop' | 'return' };

const ALL_STATUSES: ChequeStatus[] = [
  'RECEIVED', 'DEPOSITED', 'CLEARED', 'BOUNCED', 'RETURNED', 'CANCELLED', 'ISSUED', 'RELEASED', 'STOPPED',
];

/**
 * Cheque register (wave 3, lane H): received cheques (PDC, deposit, bounce, re-present) and issued
 * cheques (leaf from a cheque book, release, stop, cancel, print). Deposit and release create the
 * receipt / supplier payment; a bounce reverses it. Nothing here posts by itself.
 */
/** Help keys of the row actions, by button label (literals so the help key scan sees them). */
const CHEQUE_ACTION_HELP: Record<string, { helpKey: string }> = {
  Deposit: { helpKey: 'banking.cheques.action.deposit' },
  'Re-present': { helpKey: 'banking.cheques.action.represent' },
  Bounce: { helpKey: 'banking.cheques.action.bounce' },
  Return: { helpKey: 'banking.cheques.action.return' },
  Cancel: { helpKey: 'banking.cheques.action.cancel' },
  Print: { helpKey: 'banking.cheques.action.print' },
  Release: { helpKey: 'banking.cheques.action.release' },
  Stop: { helpKey: 'banking.cheques.action.stop' },
  'Mark cleared': { helpKey: 'banking.cheques.action.clear' },
};

const ChequeRegister: React.FC = () => {
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.BANK_ACCOUNT_MANAGE);
  const canReceipt = hasPermission(PERMISSIONS.RECEIPT_MANAGE);
  const canPayment = hasPermission(PERMISSIONS.SUPPLIER_PAYMENT_MANAGE);
  const canReconcile = hasPermission(PERMISSIONS.BANK_RECONCILIATION_MANAGE);
  const institutionId = AuthService.getSelectedInstitution()!;
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get('id');

  const [direction, setDirection] = useState<ChequeDirection>('RECEIVED');
  const [status, setStatus] = useState<string>('');
  const [bankAccountId, setBankAccountId] = useState<string>('');
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<Cheque[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [banks, setBanks] = useState<BankAccount[]>([]);
  const [dialog, setDialog] = useState<DialogKind | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      if (focusId) {
        // Deep link (?id=): show just that cheque, on its own direction tab.
        const one = await ChequeService.get(focusId);
        setDirection(one.direction);
        setRows([one]);
        setTotal(1);
        return;
      }
      const res = await ChequeService.list(institutionId, {
        direction, status: (status || undefined) as ChequeStatus | undefined,
        bankAccountId: bankAccountId || undefined, from: from || undefined, to: to || undefined,
        q: q.trim() || undefined, page, size: 25,
      });
      setRows(res.content);
      setTotal(res.totalElements);
    } catch (e) {
      toast.showApiError(e, 'Failed to load cheques');
    } finally {
      setLoading(false);
    }
  }, [institutionId, focusId, direction, status, bankAccountId, from, to, q, page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    BankAccountService.listByInstitution(institutionId).then(setBanks)
      .catch(e => toast.showApiError(e, 'Failed to load bank accounts'));
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const done = async (message: string) => {
    toast.showSuccess(message);
    setDialog(null);
    await load();
  };

  const statusOptions = useMemo(
    () => ALL_STATUSES.filter(s => (direction === 'RECEIVED'
      ? ['RECEIVED', 'DEPOSITED', 'CLEARED', 'BOUNCED', 'RETURNED', 'CANCELLED']
      : ['ISSUED', 'RELEASED', 'CLEARED', 'BOUNCED', 'STOPPED', 'CANCELLED']).includes(s)),
    [direction]);

  type Act = { label: string; onClick: () => void; color?: 'error'; disabledReason?: string };
  const actionsFor = (c: Cheque): Act[] => {
    const out: Act[] = [];
    const futureReason = futureDated(c) ? `Cheque date ${formatDate(c.chequeDate)} is in the future` : undefined;
    if (canManage) {
      if (c.direction === 'RECEIVED') {
        if (c.status === 'RECEIVED' && canReceipt) out.push({ label: 'Deposit', disabledReason: futureReason, onClick: () => setDialog({ kind: 'deposit', cheque: c }) });
        if (c.status === 'BOUNCED' && canReceipt) out.push({ label: 'Re-present', onClick: () => setDialog({ kind: 'deposit', cheque: c }) });
        if (c.status === 'DEPOSITED' && canReceipt) out.push({ label: 'Bounce', color: 'error', onClick: () => setDialog({ kind: 'bounce', cheque: c }) });
        if (c.status === 'BOUNCED') out.push({ label: 'Return', onClick: () => setDialog({ kind: 'reason', cheque: c, action: 'return' }) });
        if (c.status === 'RECEIVED' || c.status === 'BOUNCED') {
          out.push({ label: 'Cancel', color: 'error', onClick: () => setDialog({ kind: 'reason', cheque: c, action: 'cancel' }) });
        }
      } else {
        if (c.status === 'ISSUED' || c.status === 'RELEASED') out.push({ label: 'Print', onClick: () => setDialog({ kind: 'print', cheque: c }) });
        if ((c.status === 'ISSUED' || c.status === 'BOUNCED') && canPayment) out.push({ label: 'Release', disabledReason: futureReason, onClick: () => setDialog({ kind: 'release', cheque: c }) });
        if (c.status === 'RELEASED' && canPayment) out.push({ label: 'Bounce', color: 'error', onClick: () => setDialog({ kind: 'bounce', cheque: c }) });
        if (c.status === 'ISSUED' || (c.status === 'RELEASED' && canPayment)) {
          out.push({ label: 'Stop', color: 'error', onClick: () => setDialog({ kind: 'reason', cheque: c, action: 'stop' }) });
        }
        if (c.status === 'ISSUED' || c.status === 'BOUNCED') {
          out.push({ label: 'Cancel', color: 'error', onClick: () => setDialog({ kind: 'reason', cheque: c, action: 'cancel' }) });
        }
      }
    }
    if (canReconcile && c.status === (c.direction === 'RECEIVED' ? 'DEPOSITED' : 'RELEASED')) {
      out.push({
        label: 'Mark cleared',
        onClick: async () => {
          try { await ChequeService.clear(c.id); await done('Cheque marked cleared'); } catch (e) { toast.showApiError(e, 'Failed to clear cheque'); }
        },
      });
    }
    return out;
  };

  return (
    <Box data-help-page="banking.cheques.page" sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Cheques</Typography>
        {canManage && (
          <Button data-help="banking.cheques.new" variant="contained" startIcon={<AddIcon />}
            onClick={() => setDialog({ kind: direction === 'RECEIVED' ? 'registerReceived' : 'registerIssued' })}>
            {direction === 'RECEIVED' ? 'Register received cheque' : 'Write issued cheque'}
          </Button>
        )}
      </Box>
      {focusId && (
        <Chip sx={{ mb: 2 }} label="Showing one cheque (from a link). Click to show all"
          onClick={() => setSearchParams({})} onDelete={() => setSearchParams({})} />
      )}
      <Tabs data-help="banking.cheques.direction" value={direction} onChange={(_, v) => { setDirection(v); setStatus(''); setPage(0); }} sx={{ mb: 2 }}>
        <Tab value="RECEIVED" label="Received" />
        <Tab value="ISSUED" label="Issued" />
      </Tabs>

      <Box data-help-section="banking.cheques.filter" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField data-help="banking.cheques.search" label="Search" placeholder="Cheque number or party" value={q}
          onChange={e => { setQ(e.target.value); setPage(0); }} sx={{ flex: '1 1 220px', maxWidth: 300 }} />
        <TextField data-help="banking.cheques.statusFilter" select label="Status" value={status} onChange={e => { setStatus(e.target.value); setPage(0); }}
          sx={{ flex: '1 1 160px', maxWidth: 220 }}>
          <MenuItem value="">All</MenuItem>
          {statusOptions.map(s => <MenuItem key={s} value={s}>{humanizeEnum(s)}</MenuItem>)}
        </TextField>
        <TextField data-help="banking.cheques.bankFilter" select label="Bank account" value={bankAccountId}
          onChange={e => { setBankAccountId(e.target.value); setPage(0); }} sx={{ flex: '1 1 200px', maxWidth: 280 }}>
          <MenuItem value="">All</MenuItem>
          {banks.map(b => <MenuItem key={b.id} value={b.id}>{b.bankName} {b.accountNumber}</MenuItem>)}
        </TextField>
        <TextField data-help="banking.cheques.dateFrom" label="Cheque date from" type="date" value={from} InputLabelProps={{ shrink: true }}
          onChange={e => { setFrom(e.target.value); setPage(0); }} sx={{ flex: '1 1 160px', maxWidth: 200 }} />
        <TextField data-help="banking.cheques.dateTo" label="Cheque date to" type="date" value={to} InputLabelProps={{ shrink: true }}
          onChange={e => { setTo(e.target.value); setPage(0); }} sx={{ flex: '1 1 160px', maxWidth: 200 }} />
      </Box>

      {loading ? <CircularProgress /> : (
        <TableContainer data-help-section="banking.cheques.table" component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell data-help="banking.cheques.col.number">Cheque no.</TableCell>
                <TableCell data-help="banking.cheques.col.date">Cheque date</TableCell>
                <TableCell data-help="banking.cheques.col.party">{direction === 'RECEIVED' ? 'Received from' : 'Payee'}</TableCell>
                <TableCell data-help="banking.cheques.col.bank">{direction === 'RECEIVED' ? 'Drawer bank' : 'Drawn on'}</TableCell>
                <TableCell data-help="banking.cheques.col.amount" align="right">Amount</TableCell>
                <TableCell data-help="banking.cheques.col.status">Status</TableCell>
                <TableCell data-help="banking.cheques.col.presented" align="right">Presented</TableCell>
                <TableCell data-help="banking.cheques.col.actions">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(c => (
                <TableRow key={c.id} hover data-testid={`cheque-row-${c.id}`}>
                  <TableCell>{c.chequeNumber}</TableCell>
                  <TableCell>
                    {formatDate(c.chequeDate)}
                    {c.postDated && <Chip size="small" label="PDC" sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell>{c.partyName ?? '—'}</TableCell>
                  <TableCell>{(direction === 'RECEIVED' ? c.drawerBank : c.bankAccountLabel) ?? '—'}</TableCell>
                  <TableCell align="right">{formatMoney(c.amount)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={humanizeEnum(c.status)} color={CHEQUE_STATUS_COLORS[c.status]} />
                    {c.collectionsFollowUp && (
                      <Tooltip title={c.bounceReason ?? 'Bounced: follow up with the customer'}>
                        <Chip size="small" color="warning" variant="outlined" label="Collections" sx={{ ml: 1 }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">{c.presentedCount}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {actionsFor(c).map(a => (
                        a.disabledReason ? (
                          <Tooltip key={a.label} title={a.disabledReason}>
                            <span><Button {...helpElement(CHEQUE_ACTION_HELP[a.label]?.helpKey)} size="small" disabled>{a.label}</Button></span>
                          </Tooltip>
                        ) : (
                          <Button key={a.label} size="small" color={a.color} onClick={a.onClick} {...helpElement(CHEQUE_ACTION_HELP[a.label]?.helpKey)}>{a.label}</Button>
                        )
                      ))}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center">No cheques found.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination component="div" count={total} page={page} rowsPerPage={25} rowsPerPageOptions={[25]}
            onPageChange={(_, p) => setPage(p)} />
        </TableContainer>
      )}

      {dialog?.kind === 'registerReceived' && (
        <RegisterReceivedDialog institutionId={institutionId} onClose={() => setDialog(null)}
          onDone={() => done('Cheque registered')} />
      )}
      {dialog?.kind === 'registerIssued' && (
        <RegisterIssuedDialog institutionId={institutionId} banks={banks} onClose={() => setDialog(null)}
          onDone={() => done('Cheque written')} />
      )}
      {dialog?.kind === 'deposit' && (
        <DepositDialog cheque={dialog.cheque} banks={banks} onClose={() => setDialog(null)}
          onDone={() => done(dialog.cheque.status === 'BOUNCED' ? 'Cheque re-presented' : 'Cheque deposited')} />
      )}
      {dialog?.kind === 'bounce' && (
        <BounceDialog cheque={dialog.cheque} institutionId={institutionId} onClose={() => setDialog(null)}
          onDone={() => done('Cheque bounced')} />
      )}
      {dialog?.kind === 'release' && (
        <ReleaseDialog cheque={dialog.cheque} onClose={() => setDialog(null)} onDone={() => done('Cheque released')} />
      )}
      {dialog?.kind === 'print' && (
        <PrintDialog cheque={dialog.cheque} institutionId={institutionId} onClose={() => setDialog(null)}
          onDone={() => { setDialog(null); load(); }} />
      )}
      {dialog?.kind === 'reason' && (
        <ReasonDialog cheque={dialog.cheque} action={dialog.action} onClose={() => setDialog(null)}
          onDone={() => done(`Cheque ${dialog.action === 'return' ? 'returned' : dialog.action === 'stop' ? 'stopped' : 'cancelled'}`)} />
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------------------------
// Dialogs. Stacked form columns, so fullWidth is correct here (not a filter row).
// ---------------------------------------------------------------------------------------------

interface BaseDialogProps { onClose: () => void; onDone: () => void | Promise<void>; }

const RegisterReceivedDialog: React.FC<BaseDialogProps & { institutionId: string }> = ({ institutionId, onClose, onDone }) => {
  const toast = useToast();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [invoices, setInvoices] = useState<SalesInvoice[]>([]);
  const [customerId, setCustomerId] = useState('');
  const [invoiceId, setInvoiceId] = useState('');
  const [chequeNumber, setChequeNumber] = useState('');
  const [chequeDate, setChequeDate] = useState(toLocalIsoDate());
  const [amount, setAmount] = useState('');
  const [drawerBank, setDrawerBank] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    CustomerService.listByInstitution(institutionId, 0, 200, 'ALL').then(r => setCustomers(r.content))
      .catch(e => toast.showApiError(e, 'Failed to load customers'));
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setInvoiceId('');
    if (!customerId) { setInvoices([]); return; }
    SalesInvoiceService.listByCustomer(customerId)
      .then(list => setInvoices(list.filter(i => i.status === 'ISSUED' || i.status === 'PARTIALLY_PAID')))
      .catch(e => toast.showApiError(e, 'Failed to load invoices'));
  }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const valid = !!customerId && chequeNumber.trim() && drawerBank.trim() && Number(amount) > 0 && !!chequeDate;
  const save = async () => {
    try {
      setSaving(true);
      await ChequeService.registerReceived({
        institutionId, chequeNumber: chequeNumber.trim(), chequeDate, amount: Number(amount),
        drawerBank: drawerBank.trim(), customerId, salesInvoiceId: invoiceId || undefined,
      });
      await onDone();
    } catch (e) {
      toast.showApiError(e, 'Failed to register cheque');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ 'data-help-page': 'bank.chequeReceive.page' } as any}>
      <DialogTitle>Register received cheque</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }} data-help-section="bank.chequeReceive.section.cheque">
        <SearchableSelect<Customer> data-help="bank.chequeReceive.field.customer" label="Customer" options={customers} value={customerId} required
          getOptionLabel={c => c.name} onChange={id => setCustomerId(id)} />
        <SearchableSelect<SalesInvoice> data-help="bank.chequeReceive.field.invoice" label="Against invoice (optional, blank = on account)" options={invoices}
          value={invoiceId} getOptionLabel={i => `${i.invoiceNumber ?? 'Invoice'} - due ${formatMoney(i.totalAmount - i.paidAmount)}`}
          onChange={id => setInvoiceId(id)} />
        <TextField data-help="bank.chequeReceive.field.number" label="Cheque number" value={chequeNumber} required fullWidth onChange={e => setChequeNumber(e.target.value)} />
        <TextField data-help="bank.chequeReceive.field.drawerBank" label="Drawer bank" value={drawerBank} required fullWidth onChange={e => setDrawerBank(e.target.value)} />
        <TextField data-help="bank.chequeReceive.field.date" label="Cheque date" type="date" value={chequeDate} required fullWidth InputLabelProps={{ shrink: true }}
          helperText="A date after today makes this a post-dated cheque: memo only, nothing posts until you deposit it."
          onChange={e => setChequeDate(e.target.value)} />
        <TextField data-help="bank.chequeReceive.field.amount" label="Amount" type="number" value={amount} required fullWidth onChange={e => setAmount(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button data-help="bank.chequeReceive.cancel" onClick={onClose}>Cancel</Button>
        <Button data-help="bank.chequeReceive.register" variant="contained" disabled={saving || !valid} onClick={save}>Register</Button>
      </DialogActions>
    </Dialog>
  );
};

const RegisterIssuedDialog: React.FC<BaseDialogProps & { institutionId: string; banks: BankAccount[] }> = ({ institutionId, banks, onClose, onDone }) => {
  const toast = useToast();
  const [books, setBooks] = useState<ChequeBook[]>([]);
  const [bills, setBills] = useState<SupplierBill[]>([]);
  const [bookId, setBookId] = useState('');
  const [billId, setBillId] = useState('');
  const [chequeNumber, setChequeNumber] = useState('');
  const [chequeDate, setChequeDate] = useState(toLocalIsoDate());
  const [amount, setAmount] = useState('');
  const [payee, setPayee] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    ChequeBookService.list(institutionId).then(list => setBooks(list.filter(b => b.status === 'ACTIVE')))
      .catch(e => toast.showApiError(e, 'Failed to load cheque books'));
    SupplierBillService.listByInstitution(0, 200)
      .then(r => setBills(r.content.filter(b => b.status === 'UNPAID' || b.status === 'PARTIALLY_PAID')))
      .catch(e => toast.showApiError(e, 'Failed to load supplier bills'));
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const bankLabel = (b: ChequeBook) => {
    const bank = banks.find(x => x.id === b.bankAccountId);
    return `${b.bookLabel}${bank ? ` (${bank.bankName} ${bank.accountNumber})` : ''} - next ${b.nextLeaf ?? 'none'}`;
  };
  const onBill = (id: string, bill: SupplierBill | null) => {
    setBillId(id);
    if (bill) {
      if (!amount) setAmount(String(bill.totalAmount - bill.paidAmount));
      if (!payee) setPayee(bill.supplierName ?? '');
    }
  };
  const valid = !!bookId && Number(amount) > 0 && !!chequeDate && (!!billId || payee.trim().length > 0);
  const save = async () => {
    try {
      setSaving(true);
      await ChequeService.registerIssued({
        institutionId, chequeBookId: bookId, chequeNumber: chequeNumber.trim() || undefined, chequeDate,
        amount: Number(amount), supplierBillId: billId || undefined, partyName: payee.trim() || undefined,
      });
      await onDone();
    } catch (e) {
      toast.showApiError(e, 'Failed to write cheque');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ 'data-help-page': 'bank.chequeIssue.page' } as any}>
      <DialogTitle>Write issued cheque</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }} data-help-section="bank.chequeIssue.section.cheque">
        <SearchableSelect<ChequeBook> data-help="bank.chequeIssue.field.book" label="Cheque book" options={books} value={bookId} required
          getOptionLabel={bankLabel} onChange={id => setBookId(id)} />
        <TextField data-help="bank.chequeIssue.field.number" label="Cheque number (blank = next leaf)" value={chequeNumber} fullWidth
          onChange={e => setChequeNumber(e.target.value)} />
        <SearchableSelect<SupplierBill> data-help="bank.chequeIssue.field.bill" label="Supplier bill it pays (needed to release)" options={bills} value={billId}
          getOptionLabel={b => `${b.billNumber} - ${b.supplierName ?? ''} - due ${formatMoney(b.totalAmount - b.paidAmount)}`}
          onChange={onBill} />
        <TextField data-help="bank.chequeIssue.field.payee" label="Payee" value={payee} fullWidth required={!billId} onChange={e => setPayee(e.target.value)} />
        <TextField data-help="bank.chequeIssue.field.date" label="Cheque date" type="date" value={chequeDate} required fullWidth InputLabelProps={{ shrink: true }}
          helperText="A date after today is a post-dated cheque: it can only be released on or after that date."
          onChange={e => setChequeDate(e.target.value)} />
        <TextField data-help="bank.chequeIssue.field.amount" label="Amount" type="number" value={amount} required fullWidth onChange={e => setAmount(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button data-help="bank.chequeIssue.cancel" onClick={onClose}>Cancel</Button>
        <Button data-help="bank.chequeIssue.write" variant="contained" disabled={saving || !valid} onClick={save}>Write cheque</Button>
      </DialogActions>
    </Dialog>
  );
};

const DepositDialog: React.FC<BaseDialogProps & { cheque: Cheque; banks: BankAccount[] }> = ({ cheque, banks, onClose, onDone }) => {
  const toast = useToast();
  const [bankId, setBankId] = useState(cheque.bankAccountId ?? '');
  const [date, setDate] = useState(toLocalIsoDate());
  const [saving, setSaving] = useState(false);
  const save = async () => {
    try {
      setSaving(true);
      await ChequeService.deposit(cheque.id, bankId, date);
      await onDone();
    } catch (e) {
      toast.showApiError(e, 'Failed to deposit cheque');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ 'data-help-page': 'bank.chequeDeposit.page' } as any}>
      <DialogTitle>{cheque.status === 'BOUNCED' ? 'Re-present' : 'Deposit'} cheque {cheque.chequeNumber}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }} data-help-section="bank.chequeDeposit.section.deposit">
        <Typography variant="body2">
          Creates a receipt of {formatMoney(cheque.amount)} against {cheque.salesInvoiceId ? 'the invoice' : 'the customer account (on account)'}.
        </Typography>
        <SearchableSelect<BankAccount> data-help="bank.chequeDeposit.field.bank" label="Deposit into bank account" options={banks} value={bankId} required
          getOptionLabel={b => `${b.bankName} ${b.accountNumber}`} onChange={id => setBankId(id)} />
        <TextField data-help="bank.chequeDeposit.field.date" label="Deposit date" type="date" value={date} fullWidth InputLabelProps={{ shrink: true }}
          onChange={e => setDate(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button data-help="bank.chequeDeposit.cancel" onClick={onClose}>Cancel</Button>
        <Button data-help="bank.chequeDeposit.deposit" variant="contained" disabled={saving || !bankId || !date} onClick={save}>
          {cheque.status === 'BOUNCED' ? 'Re-present' : 'Deposit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const BounceDialog: React.FC<BaseDialogProps & { cheque: Cheque; institutionId: string }> = ({ cheque, onClose, onDone }) => {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(toLocalIsoDate());
  const [charge, setCharge] = useState('');
  const [chargeAccountId, setChargeAccountId] = useState('');
  const [expenseAccounts, setExpenseAccounts] = useState<Account[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await AccountService.getAccountsByType('EXPENSE');
        setExpenseAccounts(leafAccounts(Array.isArray(res) ? res : (res?.content ?? [])));
      } catch (e) {
        toast.showApiError(e, 'Failed to load accounts');
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hasCharge = Number(charge) > 0;
  const valid = reason.trim().length > 0 && (!hasCharge || !!chargeAccountId);
  const save = async () => {
    try {
      setSaving(true);
      await ChequeService.bounce(cheque.id, {
        bounceDate: date, reason: reason.trim(),
        chargeAmount: hasCharge ? Number(charge) : undefined, chargeAccountId: hasCharge ? chargeAccountId : undefined,
      });
      await onDone();
    } catch (e) {
      toast.showApiError(e, 'Failed to bounce cheque');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ 'data-help-page': 'bank.chequeBounce.page' } as any}>
      <DialogTitle>Bounce cheque {cheque.chequeNumber}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }} data-help-section="bank.chequeBounce.section.bounce">
        <Typography variant="body2">
          {cheque.direction === 'RECEIVED'
            ? 'Reverses the receipt (the invoice becomes outstanding again) and flags the cheque for collections.'
            : 'Reverses the supplier payment (the bill becomes payable again).'}
        </Typography>
        <TextField data-help="bank.chequeBounce.field.reason" label="Reason" value={reason} required fullWidth multiline minRows={2}
          onChange={e => setReason(e.target.value)} />
        <TextField data-help="bank.chequeBounce.field.date" label="Bounce date" type="date" value={date} fullWidth InputLabelProps={{ shrink: true }}
          onChange={e => setDate(e.target.value)} />
        <TextField data-help="bank.chequeBounce.field.charge" label="Bank charge (optional)" type="number" value={charge} fullWidth
          onChange={e => setCharge(e.target.value)} />
        {hasCharge && (
          <AccountAutocomplete data-help="bank.chequeBounce.field.chargeAccount" accounts={expenseAccounts} value={chargeAccountId} label="Bank charges account" required
            onChange={id => setChargeAccountId(id)} />
        )}
      </DialogContent>
      <DialogActions>
        <Button data-help="bank.chequeBounce.cancel" onClick={onClose}>Cancel</Button>
        <Button data-help="bank.chequeBounce.bounce" variant="contained" color="error" disabled={saving || !valid} onClick={save}>Bounce</Button>
      </DialogActions>
    </Dialog>
  );
};

const ReleaseDialog: React.FC<BaseDialogProps & { cheque: Cheque }> = ({ cheque, onClose, onDone }) => {
  const toast = useToast();
  const [bills, setBills] = useState<SupplierBill[]>([]);
  const [billId, setBillId] = useState(cheque.supplierBillId ?? '');
  const [date, setDate] = useState(toLocalIsoDate());
  const [saving, setSaving] = useState(false);
  const beforeChequeDate = !!date && !!cheque.chequeDate && date < cheque.chequeDate.slice(0, 10);   // polish 3: the hint only shows when it matters
  const [withholding, setWithholding] = useState<PaymentWithholdingValue>({ lines: [], total: 0, pending: false }); // B4-0 (0-3)
  // B4-0 (H1): the cheque face is the NET cash; the settled (gross) amount is face + withheld, withheld on that gross,
  // so it is converged here by feeding the panel the latest gross until the withheld total stops moving.
  const [gross, setGross] = useState<number>(cheque.amount);
  const rounds = useRef(0);
  useEffect(() => { rounds.current = 0; setGross(cheque.amount); }, [billId, cheque.amount]);
  useEffect(() => {
    if (withholding.pending) return;
    const next = Math.round((cheque.amount + withholding.total) * 100) / 100;
    if (Math.abs(next - gross) > 0.004 && rounds.current < 12) {
      rounds.current += 1;
      setGross(next);
    }
  }, [withholding, gross, cheque.amount]);

  useEffect(() => {
    SupplierBillService.listByInstitution(0, 200)
      .then(r => setBills(r.content.filter(b => b.status === 'UNPAID' || b.status === 'PARTIALLY_PAID' || b.id === cheque.supplierBillId)))
      .catch(e => toast.showApiError(e, 'Failed to load supplier bills'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    try {
      setSaving(true);
      await ChequeService.release(cheque.id, date, billId || undefined,
        withholding.lines.length > 0 ? withholding.lines : undefined);
      await onDone();
    } catch (e) {
      toast.showApiError(e, 'Failed to release cheque');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ 'data-help-page': 'bank.chequeRelease.page' } as any}>
      <DialogTitle>Release cheque {cheque.chequeNumber}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }} data-help-section="bank.chequeRelease.section.release">
        <Typography variant="body2">
          Hands the cheque to the supplier, credits the bank {formatMoney(cheque.amount)} (the cheque amount) and settles the bill by that amount plus any withholding.
        </Typography>
        <SearchableSelect<SupplierBill> data-help="bank.chequeRelease.field.bill" label="Supplier bill" options={bills} value={billId} required
          getOptionLabel={b => `${b.billNumber} - ${b.supplierName ?? ''} - due ${formatMoney(b.totalAmount - b.paidAmount)}`}
          onChange={id => setBillId(id)} />
        {/* ---- B4-0 (0-3): VDS / TDS deducted on release, as on the supplier-payment form ---- */}
        <PaymentWithholdingSection billId={billId || null} amount={gross} onChange={setWithholding} />
        {withholding.lines.length > 0 && (
          <Typography variant="body2" data-testid="release-withholding-summary">
            Cheque (net to bank) {formatMoney(cheque.amount)} + withheld {formatMoney(withholding.total)} = settled against the bill {formatMoney(cheque.amount + withholding.total)}
          </Typography>
        )}
        {/* ---- end B4-0 ---- */}
        <TextField data-help="bank.chequeRelease.field.date" label="Release date" type="date" value={date} fullWidth InputLabelProps={{ shrink: true }}
          error={beforeChequeDate}
          helperText={beforeChequeDate ? `A post-dated cheque cannot be released before its date (${formatDate(cheque.chequeDate)}).` : undefined}
          onChange={e => setDate(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button data-help="bank.chequeRelease.cancel" onClick={onClose}>Cancel</Button>
        <Button data-help="bank.chequeRelease.release" variant="contained" disabled={saving || withholding.pending || !billId || !date || beforeChequeDate} onClick={save}>Release</Button>
      </DialogActions>
    </Dialog>
  );
};

const PrintDialog: React.FC<{ cheque: Cheque; institutionId: string; onClose: () => void; onDone: () => void }> = ({ cheque, institutionId, onClose, onDone }) => {
  const toast = useToast();
  const [layouts, setLayouts] = useState<ChequeLayout[]>([]);
  const [layoutId, setLayoutId] = useState('');
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    ChequeLayoutService.list(institutionId).then(list => {
      setLayouts(list);
      setLayoutId(list.find(l => l.defaultLayout)?.id ?? list[0]?.id ?? '');
    }).catch(e => toast.showApiError(e, 'Failed to load layouts'));
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const print = async () => {
    try {
      setPrinting(true);
      const blob = await ChequeService.print(cheque.id, layoutId || undefined);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      onDone();
    } catch (e) {
      toast.showApiError(e, 'Failed to print cheque');
    } finally {
      setPrinting(false);
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ 'data-help-page': 'bank.chequePrint.page' } as any}>
      <DialogTitle>Print cheque {cheque.chequeNumber}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }} data-help-section="bank.chequePrint.section.print">
        {layouts.length === 0 && (
          <Typography variant="body2" color="warning.main">
            No print layout yet. Open Cheque Layouts and use "Load default layouts".
          </Typography>
        )}
        <SearchableSelect<ChequeLayout> data-help="bank.chequePrint.field.layout" label="Layout" options={layouts} value={layoutId} required
          getOptionLabel={l => l.layoutName} onChange={id => setLayoutId(id)} />
        {cheque.printedAt && <Typography variant="caption">Printed before on {formatDate(cheque.printedAt)}.</Typography>}
      </DialogContent>
      <DialogActions>
        <Button data-help="bank.chequePrint.close" onClick={onClose}>Close</Button>
        <Button data-help="bank.chequePrint.print" variant="contained" disabled={printing || !layoutId} onClick={print}>Print</Button>
      </DialogActions>
    </Dialog>
  );
};

/** Help keys of the reason dialog, by action (literals so the help key scan sees them). */
const REASON_HELP: Record<'cancel' | 'stop' | 'return', { paper: Record<string, string>; helpKey: string }> = {
  cancel: { paper: { 'data-help-page': 'bank.chequeCancel.page' }, helpKey: 'bank.chequeCancel.confirm' },
  stop: { paper: { 'data-help-page': 'bank.chequeStop.page' }, helpKey: 'bank.chequeStop.confirm' },
  return: { paper: { 'data-help-page': 'bank.chequeReturn.page' }, helpKey: 'bank.chequeReturn.confirm' },
};

const ReasonDialog: React.FC<BaseDialogProps & { cheque: Cheque; action: 'cancel' | 'stop' | 'return' }> = ({ cheque, action, onClose, onDone }) => {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const label = action === 'return' ? 'Return to customer' : action === 'stop' ? 'Stop payment' : 'Cancel cheque';
  const save = async () => {
    try {
      setSaving(true);
      if (action === 'return') await ChequeService.markReturned(cheque.id, reason.trim() || undefined);
      else if (action === 'stop') await ChequeService.stop(cheque.id, reason.trim() || undefined);
      else await ChequeService.cancel(cheque.id, reason.trim() || undefined);
      await onDone();
    } catch (e) {
      toast.showApiError(e, `Failed: ${label.toLowerCase()}`);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth PaperProps={REASON_HELP[action].paper as any}>
      <DialogTitle>{label} {cheque.chequeNumber}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }} data-help-section="bank.chequeReason.section.reason">
        {action === 'stop' && cheque.status === 'RELEASED' && (
          <Typography variant="body2">The payment made on release is reversed and the bill becomes payable again.</Typography>
        )}
        <TextField data-help="bank.chequeReason.field.reason" label="Reason (optional)" value={reason} fullWidth multiline minRows={2}
          onChange={e => setReason(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button data-help="bank.chequeReason.back" onClick={onClose}>Back</Button>
        <Button {...helpElement(REASON_HELP[action].helpKey)} variant="contained" color={action === 'return' ? 'primary' : 'error'} disabled={saving} onClick={save}>{label}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChequeRegister;
